/**
 * useCrossBrowserSpeech
 *
 * Provides a unified speech recognition interface that works in ALL browsers:
 *
 *   Chrome / Edge (Chromium)  → native Web Speech API via react-speech-recognition
 *   Firefox / Safari / Others → MediaRecorder → server /transcribe → Google STT
 *
 * Returns the same shape as react-speech-recognition so App.js barely changes.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

const ENDPOINT = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

// Detect native Web Speech API support (Chrome/Edge only in practice)
const hasNativeSTT = () =>
  typeof window !== 'undefined' &&
  (!!window.SpeechRecognition || !!window.webkitSpeechRecognition);

/* ─────────────────────────────────────────────
   Fallback: MediaRecorder + server transcription
   Records in 4-second chunks, sends to /transcribe
───────────────────────────────────────────── */
const useMediaRecorderSTT = (lang, onFinalTranscript) => {
  const mediaRecorderRef = useRef(null);
  const streamRef        = useRef(null);
  const chunksRef        = useRef([]);
  const activeRef        = useRef(false);
  const langRef          = useRef(lang);
  const onFinalRef       = useRef(onFinalTranscript);
  const intervalRef      = useRef(null);

  useEffect(() => { langRef.current = lang; }, [lang]);
  useEffect(() => { onFinalRef.current = onFinalTranscript; }, [onFinalTranscript]);

  // Send collected chunks to server for transcription
  const flushChunks = useCallback(async (chunks, mimeType) => {
    if (!chunks.length || !activeRef.current) return;
    const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
    if (blob.size < 1000) return; // skip near-empty recordings

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result.split(',')[1];
      try {
        const res = await fetch(`${ENDPOINT}/transcribe`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ audio: base64, lang: langRef.current, mimeType }),
        });
        const data = await res.json();
        if (data.transcript && data.transcript.trim()) {
          onFinalRef.current(data.transcript.trim());
        }
      } catch (err) {
        console.error('[STT fallback] Transcription error:', err);
      }
    };
    reader.readAsDataURL(blob);
  }, []);

  const startListening = useCallback(async () => {
    if (activeRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      // Pick best supported mime type
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
        .find(t => MediaRecorder.isTypeSupported(t)) || '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      // Every 4 seconds: flush current chunks, start fresh
      recorder.onstart = () => {
        intervalRef.current = setInterval(() => {
          if (!activeRef.current) return;
          recorder.requestData(); // trigger ondataavailable
          const currentChunks = [...chunksRef.current];
          chunksRef.current = [];
          flushChunks(currentChunks, mimeType);
        }, 4000);
      };

      recorder.start(500); // collect in 500ms slices
      activeRef.current = true;
    } catch (err) {
      console.error('[STT fallback] Mic access error:', err);
    }
  }, [flushChunks]);

  const stopListening = useCallback(() => {
    activeRef.current = false;
    clearInterval(intervalRef.current);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
      // Flush any remaining audio
      setTimeout(() => {
        const currentChunks = [...chunksRef.current];
        chunksRef.current = [];
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        flushChunks(currentChunks, mimeType);
      }, 600);
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, [flushChunks]);

  return { startListening, stopListening };
};

/* ─────────────────────────────────────────────
   Main hook — unified interface
───────────────────────────────────────────── */
const useCrossBrowserSpeech = () => {
  const isNative = hasNativeSTT();

  // ── Native (Chrome) path ──
  const {
    finalTranscript: nativeFinal,
    interimTranscript: nativeInterim,
    resetTranscript: nativeReset,
  } = useSpeechRecognition();

  // ── Fallback path ──
  const [fallbackFinal,   setFallbackFinal]   = useState('');
  const [fallbackInterim, setFallbackInterim] = useState('');
  const langRef = useRef('en-US');

  const handleFallbackFinal = useCallback((text) => {
    setFallbackFinal(text);
    // Auto-clear after consumer reads it (mimics react-speech-recognition behaviour)
    setTimeout(() => setFallbackFinal(''), 100);
  }, []);

  const { startListening: fbStart, stopListening: fbStop } =
    useMediaRecorderSTT(langRef.current, handleFallbackFinal);

  // ── Unified controls ──
  const startListening = useCallback(({ continuous, language } = {}) => {
    langRef.current = language || 'en-US';
    if (isNative) {
      SpeechRecognition.startListening({ continuous: continuous ?? true, language });
    } else {
      fbStart();
    }
  }, [isNative, fbStart]);

  const stopListening = useCallback(() => {
    if (isNative) {
      SpeechRecognition.stopListening();
    } else {
      fbStop();
    }
  }, [isNative, fbStop]);

  const resetTranscript = useCallback(() => {
    if (isNative) nativeReset();
    else { setFallbackFinal(''); setFallbackInterim(''); }
  }, [isNative, nativeReset]);

  return {
    finalTranscript:   isNative ? nativeFinal   : fallbackFinal,
    interimTranscript: isNative ? nativeInterim : fallbackInterim,
    resetTranscript,
    startListening,
    stopListening,
    isNative,
    browserSupportsSpeechRecognition: true, // always true — we handle it ourselves
  };
};

export default useCrossBrowserSpeech;